import * as React from "react"
import { useStaticQuery, graphql, Link as RouterLink } from "gatsby"
import { StaticImage } from "gatsby-plugin-image";
import { Container, Typography, Button, Menu, MenuItem, ThemeProvider, IconButton, Toolbar, Link, Stack, ListItemIcon, ListItemText, Box, Divider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ConfirmProvider } from "material-ui-confirm";
import { SnackbarProvider } from 'notistack';
import { SWRConfig } from 'swr';
import { ErrorBoundary } from "react-error-boundary";
import CookieConsent from "react-cookie-consent";

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LoginIcon from '@mui/icons-material/Login';
import MenuIcon from '@mui/icons-material/Menu';

import Footer from './Footer'
import theme from './theme';
import ErrorFallback from "./ErrorFallback";
import Tracking from "./Tracking";

const fetcher = async (url, data) => {
  const api_url = process.env.NODE_ENV === 'development' ? "http://localhost:8080" : "";

  const res = await fetch(api_url + url, {
    method: data?.arg ? 'POST' : 'GET',
    body: data?.arg ? JSON.stringify(data?.arg) : null,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    const data = await res.json();

    const error = new Error(data?.user?.message ?? '');
    error.user = data?.user;
    error.fields = data?.fields;
    error.status = res.status;
    throw error;
  }

  return res.json();
};

const SWR_OPTIONS = {
  fetcher,
  revalidateOnFocus: false,
};

const Layout = ({ location, children }) => {
  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
      logo: file(relativePath: { eq: "dontpanic-ferris-logo.svg" }) {
        publicURL
      }
    }
  `)

  const home = location.pathname === '/'

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <SWRConfig value={SWR_OPTIONS}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
          <ConfirmProvider>
            <Box height="100vh" display="flex" flexDirection="column">

              <ErrorBoundary FallbackComponent={ErrorFallback}>

                <Container maxWidth="lg">
                  <Toolbar variant="dense" disableGutters={true} sx={{ py: 2 }}>

                    <IconButton edge="start" sx={{ mr: 1, ml: 0 }} component={RouterLink} to="/">
                      <Box
                        component="img"
                        src={data.logo.publicURL}
                        alt="Don't Panic Logo of a panicking ferris"
                        sx={{
                          width: '29px',
                          height: '29px',
                          display: 'flex'
                        }}
                      />
                    </IconButton>

                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color="inherit"
                      sx={{ textDecoration: 'none' }}
                      component={RouterLink}
                      to="/"
                    >
                      {"Don't Panic"}
                    </Typography>

                    <Box sx={{ flex: 1 }}></Box>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                      <Button
                        sx={{ textTransform: 'none', fontWeight: 400, fontSize: 15 }}
                        component={RouterLink}
                        to="/contact"
                        color="inherit"
                      >
                        Get In Touch
                      </Button>
                      <Button
                        sx={{ textTransform: 'none', fontWeight: 400, fontSize: 15 }}
                        component={RouterLink}
                        to="/pricing"
                        color="inherit"
                      >
                        Pricing
                      </Button>
                      <ResourcesMenu />

                      <Button
                        component={Link}
                        href="https://app.dontpanic.rs"
                        variant="contained"
                        sx={{ borderRadius: 50, ml: 2 }}
                      >
                        Log In
                      </Button>
                    </Stack>

                    <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                      <IconButton
                        size="large"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleOpenMenu}
                        color="inherit"
                      >
                        <MenuIcon />
                      </IconButton>
                      <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        keepMounted
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}
                        sx={{ display: { xs: 'block', md: 'none' } }}
                      >
                        <MenuItem component={RouterLink} to="/contact" onClick={handleCloseMenu}>
                          <Typography sx={{ textAlign: 'center' }}>Get In Touch</Typography>
                        </MenuItem>

                        <MenuItem component={RouterLink} to="/pricing" onClick={handleCloseMenu}>
                          <Typography sx={{ textAlign: 'center' }}>Pricing</Typography>
                        </MenuItem>

                        <Divider />

                        <MenuItem component={Link} onClick={handleCloseMenu} href="https://github.com/peterprototypes/dontpanic-server">
                          <ListItemIcon>
                            <StaticImage
                              src="../images/github-mark.svg"
                              alt="Github"
                              style={{ width: '20px' }}
                            />
                          </ListItemIcon>
                          <ListItemText>{"Don't Panic Server"}</ListItemText>
                        </MenuItem>

                        <MenuItem component={Link} onClick={handleCloseMenu} href="https://crates.io/crates/dontpanic">
                          <ListItemIcon><Box component="img" src="https://crates.io/favicon.ico" sx={{ width: '20px' }} /></ListItemIcon>
                          <ListItemText>Library at crates.io</ListItemText>
                        </MenuItem>

                        <MenuItem component={Link} onClick={handleCloseMenu} href="https://docs.rs/dontpanic/latest/dontpanic">
                          <ListItemIcon><Box component="img" src="https://docs.rs/-/rustdoc.static/favicon-32x32-422f7d1d52889060.png" sx={{ width: '20px' }} /></ListItemIcon>
                          <ListItemText>Documentation</ListItemText>
                        </MenuItem>

                        <Divider />

                        <MenuItem component={Link} onClick={handleCloseMenu} href="https://app.dontpanic.rs">
                          <ListItemIcon><LoginIcon /></ListItemIcon>
                          <ListItemText>Log In</ListItemText>
                        </MenuItem>
                      </Menu>
                    </Box>


                  </Toolbar>
                </Container>
                {home ? (
                  <Box sx={{ flexGrow: 1, py: 3 }}>
                    {children}
                  </Box>
                ) : (
                  <Container maxWidth="lg" sx={{ flexGrow: 1, py: 3 }}>
                    {children}
                  </Container>
                )}

                <Footer />

                <Tracking location={location} />

                <CookieConsent
                  buttonStyle={{ marginTop: '0', color: 'white', backgroundColor: '#0071e3' }}
                  style={{ margin: '5px', width: '300px', background: 'white', borderRadius: '10px', color: 'rgba(0, 0, 0, 0.6)', boxShadow: '0 16px 55px rgba(0, 0, 0, .1)' }}
                >
                  This website uses cookies to enhance the user experience.
                </CookieConsent>
              </ErrorBoundary>
            </Box>
          </ConfirmProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </SWRConfig>
  )
}

const ResourcesMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{ textTransform: 'none', fontWeight: 400, fontSize: 15 }}
        endIcon={<KeyboardArrowDownIcon />}
        color="inherit"
      >
        Resources
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem component={Link} onClick={handleClose} href="https://github.com/peterprototypes/dontpanic-server">
          <ListItemIcon>
            <StaticImage
              src="../images/github-mark.svg"
              alt="Github"
              style={{ width: '20px' }}
            />
          </ListItemIcon>
          <ListItemText>{"Don't Panic Server"}</ListItemText>
        </MenuItem>

        <MenuItem component={Link} onClick={handleClose} href="https://crates.io/crates/dontpanic">
          <ListItemIcon><Box component="img" src="https://crates.io/favicon.ico" sx={{ width: '20px' }} /></ListItemIcon>
          <ListItemText>Library at crates.io</ListItemText>
        </MenuItem>

        <MenuItem component={Link} onClick={handleClose} href="https://docs.rs/dontpanic/latest/dontpanic">
          <ListItemIcon><Box component="img" src="https://docs.rs/-/rustdoc.static/favicon-32x32-422f7d1d52889060.png" sx={{ width: '20px' }} /></ListItemIcon>
          <ListItemText>Documentation</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default Layout
